import { Col, Radio, Row, Tooltip, Card, Form } from "antd";
import { Procedure, SurgeonProcedure } from "../../../types/procedures";
import { procedureDisplayName } from "../../../utils/procedureDisplayName";
import { Surgeon } from "../../../types/surgeons";
import { useQuote } from "../QuoteProvider";
import { StyledRadio } from "../StyledQuoteInput";
import QuoteSummary from "../QuoteSummary";
import QuoteSurgeon from "../components/QuoteSurgeon/QuoteSurgeon";
import QuoteDateAvailable from "../components/QuoteDateAvailable/QuoteDateAvailable";
import QuoteButton from "../components/QuoteButton/QuoteButton";
import { useAppConfig } from "../../hooks/useAppConfig";
import TextArea from "antd/es/input/TextArea";
import { useIsMobile } from "../MobileBreakpointContext";
// @ts-ignore
import { useTranslation } from "react-i18next";
import "./scss/step-select-surgeon.scss";

type StepSelectSurgeonParams = {
  next: () => void;
};

const StepSelectSurgeon = ({ next }: StepSelectSurgeonParams) => {
  const quoteData = useQuote();
  const isMobile = useIsMobile();
  const { data: appConfig } = useAppConfig();
  // @ts-ignore
  const { t: tProcedures, i18n } = useTranslation("procedures");

  const approx = quoteData.getSurgeonPricing().minPrice || 0;

  const data = {
    surgeonMinPrice: quoteData.getSurgeonPricing().minPrice,
    surgeonMaxPrice: quoteData.getSurgeonPricing().maxPrice,
    approximate: approx + approx * 0.15,
    daysUntilAvailable: quoteData.selectedSurgeon?.daysUntilAvailable,
  };

  const onChange = (e: any) => {
    quoteData.updateSurgeon(e.target.value);
  };

  const selectedProcedures = quoteData.selectedProcedures?.map(
    (procedure: Procedure) => {
      return procedure.name;
    },
  );

  return (
    <div className="step-select-surgeon">
      <Row>
        <Col
          xl={14}
          lg={24}
          offset={!isMobile ? 1 : 0}
          style={{ padding: 0 }}
        >
          <h1 className="step-select-surgeon__heading">Choose your surgeon</h1>
          <Radio.Group
            name="surgeon"
            className="quote-surgeon-select-container"
            onChange={onChange}
            value={quoteData.selectedSurgeon?.id}
          >
            {quoteData.surgeons.map((surgeon: Surgeon) => {
              //check if surgeon is available in the location
              const surgeonInThisLocation = surgeon.businessLocations.some(
                (location) => {
                  return location.id === quoteData.location;
                },
              );

              if (surgeonInThisLocation === false) return null;

              const foundProcedures: Procedure[] = [];
              const notFoundProcedures: Procedure[] = [];

              quoteData.selectedProcedures.forEach((proc) => {
                // for each selectedProcedure check if prices has surgeon
                const found = proc.prices?.some((price: SurgeonProcedure) => {
                  if (price.surgeon) {
                    return price.surgeon.id === surgeon.id && price.price > 0;
                  }
                  return false;
                });

                if (found) {
                  foundProcedures.push(proc);
                } else {
                  notFoundProcedures.push(proc);
                }
              });

              const disableSurgeon = notFoundProcedures.length > 0;

              const childContent = (
                <StyledRadio
                  style={{ ...(!isMobile ? { maxWidth: "34%" } : {}) }}
                  value={surgeon.id}
                  disabled={disableSurgeon}
                  className="quote-surgeon-radio-container"
                >
                  <QuoteSurgeon isDisabled={disableSurgeon} surgeon={surgeon} />
                </StyledRadio>
              );

              const unavailableItems = (
                <div>
                  <div style={{ fontWeight: "bold" }}>
                    This procedure/s is not available by this surgeon:
                  </div>
                  <div>
                    {notFoundProcedures.map((nf: Procedure) => (
                      <div key={nf.id}>
                        - {procedureDisplayName(nf, { language: i18n?.language, t: tProcedures })}
                      </div>
                    ))}
                  </div>
                </div>
              );

              return disableSurgeon ? (
                <Tooltip title={unavailableItems} color="pink" key={surgeon.id}>
                  {childContent}
                </Tooltip>
              ) : (
                childContent
              );
            })}
          </Radio.Group>

          {/* <div
            style={{
              color: "black",
              fontSize: "18px",
              padding: "20px 40px 10px 40px",
              lineHeight: "30px",
              textAlign: "left",
            }}
          >
            <p className="quote-surgeon-tagline" style={{}}>
              At {appConfig?.title}, all our skilled surgeons are committed to
              ensuring equally <b>outstanding results</b>.
            </p>
          </div> */}
          <div
            style={{
              paddingRight: !isMobile ? "20px" : 0,
            }}
          >
            {/* <QuoteDateAvailable /> */}
            {!isMobile && (
              <QuoteButton
                disabled={quoteData.selectedSurgeon === undefined}
                onClick={next}
              >
                {/* Do not show video if its Dr Miami */}
                {quoteData.selectedSurgeon?.id === "64f7fac0f5661576d884ff89" ||
                quoteData.selectedSurgeon?.id === "64e6102a6c9456cc2fa181f2"
                  ? "Get free consultation"
                  : "Book free video consultation"}
              </QuoteButton>
            )}
          </div>
        </Col>

        <Col span={isMobile ? 24 : 8} style={{ padding: "0", paddingBottom: "30px" }}>
          {(quoteData.selectedSurgeon || !isMobile) && <QuoteSummary />}

          <div
            style={{ paddingBottom: isMobile ? "80px" : 0 }}
          >
            {isMobile && (
              <Card className="step-select-surgeon__describe-card">
                <Form layout="vertical">
                  <Form.Item label="Describe your procedure in detail:">
                    <TextArea
                      value={quoteData.procedureDescription}
                      onChange={(
                        event: React.ChangeEvent<HTMLTextAreaElement>,
                      ) => {
                        quoteData.setProcedureDescription(event.target.value);
                      }}
                    />
                  </Form.Item>
                </Form>
              </Card>
            )}
          </div>
          {isMobile && (
            <div className="quote-button-mobile-container">
              <QuoteButton
                disabled={quoteData.selectedSurgeon === undefined}
                onClick={next}
              >
                {quoteData.selectedSurgeon?.id === "64f7fac0f5661576d884ff89" ||
                quoteData.selectedSurgeon?.id === "64e6102a6c9456cc2fa181f2"
                  ? "Get free consultation"
                  : "Book free video consultation"}
              </QuoteButton>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default StepSelectSurgeon;
